import { ActionIcon, Indicator, Tooltip } from "@mantine/core"
import { modals } from "@mantine/modals"
import { IconChartCovariate, IconWand } from "@tabler/icons-react"
import _ from "lodash"
import { useCallback } from "react"
import { RuleGroupType } from "react-querybuilder"

import { BillyResponses } from "@costory/types/endpoints/billy"
import { Position } from "@costory/types/endpoints/digest"
import { Filters } from "@costory/types/filters"
import {
  BillyContributionAnalysisCategory,
  DatePreset,
} from "@costory/types/prisma-client"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs, { Dayjs } from "@costory/shared/dayjs"
import { getdeterministicNodeId } from "@costory/shared/utils/digest"
import { getFiltersFromPosition } from "@costory/shared/utils/filters"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useBilly } from "@costory/front/pages/DigestV2/components/SuggestedCards/hooks/useBilly"
import { getQueryFormatted } from "@costory/front/pages/DigestV2/helper"

export const ContributionAnalysisWand = ({
  position,
  datePreset,
  period,
  setMarkdownContent,
  exclusionWhereClause,
  costColumn,
  setSummaryValueFromBilly,
}: {
  position: Position
  datePreset: DatePreset
  period: string
  setMarkdownContent: (content: string) => void
  exclusionWhereClause: RuleGroupType
  costColumn: string
  setSummaryValueFromBilly: (summary: string) => void
}) => {
  const {
    queryContributionAnalysis,
    queryHistoryOfCosts,
    mutationContributionAnalysis,
    mutationHistoryOfCosts,
    digestPeriod,
  } = useBilly({
    position,
    datePreset,
    period,
    exclusionWhereClause,
    costColumn,
  })
  const convertSummaryContributingFactorsPartial = useCallback(
    (summary: string, contributors: BillyResponses.Contributors) => {
      return convertSummaryContributingFactors(
        summary,
        contributors,
        position,
        digestPeriod,
      )
    },
    [position, digestPeriod],
  )
  const triggerAnalysisContributionAnalysis = () => {
    modals.openConfirmModal({
      title: "Trigger Contribution Analysis",
      children:
        "this will trigger a new contribution analysis run. This may take a few minutes (2-3 minutes)",
      labels: { confirm: "Yes, trigger", cancel: "Cancel" },
      confirmProps: { color: "blue" },
      onConfirm: () => mutationContributionAnalysis.mutate(),
    })
  }
  const triggerAnalysisHistoryOfCosts = () => {
    modals.openConfirmModal({
      title: "Trigger History of Costs",
      children:
        "this will trigger a new history of costs run. This may take a few minutes (2-3 minutes)",
      labels: { confirm: "Yes, trigger", cancel: "Cancel" },
      confirmProps: { color: "blue" },
      onConfirm: () => mutationHistoryOfCosts.mutate(),
    })
  }
  return (
    <>
      <QueryWrapper
        query={queryHistoryOfCosts}
        defaultData={[]}
        allowEmptyArray
      >
        {({ data }) => {
          return (
            <Component
              result={data.filter(
                (item) =>
                  getdeterministicNodeId(item.whereClause) ==
                  getdeterministicNodeId(position),
              )}
              setMarkdownContent={setMarkdownContent}
              triggerAnalysis={triggerAnalysisHistoryOfCosts}
              convertSummaryContributingFactorsPartial={
                convertSummaryContributingFactorsPartial
              }
              Icon={<IconChartCovariate />}
              category={BillyContributionAnalysisCategory.HISTORY_OF_COSTS}
              setSummaryValueFromBilly={setSummaryValueFromBilly}
            />
          )
        }}
      </QueryWrapper>
      <QueryWrapper
        query={queryContributionAnalysis}
        defaultData={[]}
        allowEmptyArray
      >
        {({ data }) => {
          return (
            <Component
              result={data.filter(
                (item) =>
                  getdeterministicNodeId(item.whereClause) ==
                  getdeterministicNodeId(position),
              )}
              setMarkdownContent={setMarkdownContent}
              triggerAnalysis={triggerAnalysisContributionAnalysis}
              convertSummaryContributingFactorsPartial={
                convertSummaryContributingFactorsPartial
              }
              Icon={<IconWand />}
              category={BillyContributionAnalysisCategory.CONTRIBUTION_ANALYSIS}
              setSummaryValueFromBilly={setSummaryValueFromBilly}
            />
          )
        }}
      </QueryWrapper>
    </>
  )
}

const Component = ({
  result,
  setMarkdownContent,
  triggerAnalysis,
  convertSummaryContributingFactorsPartial,
  Icon,
  category,
  setSummaryValueFromBilly,
}: {
  result: BillyResponses.ContributionAnalysisAvailable[]
  setMarkdownContent: (content: string) => void
  triggerAnalysis: () => void
  convertSummaryContributingFactorsPartial: (
    summary: string,
    contributors: BillyResponses.Contributors,
  ) => string
  Icon: React.ReactNode
  category: BillyContributionAnalysisCategory
  setSummaryValueFromBilly: (summary: string) => void
}) => {
  const lastVersions = _(
    result.map((item) => ({
      summary:
        "data" in item.result
          ? item.result.data && item.result.data.summary
          : "",
      summaryTitle:
        "data" in item.result &&
        item.result.data &&
        "summary_title" in item.result.data
          ? item.result.data.summary_title
          : "",
      updatedAt: item.updatedAt,
      status: item.status,
      contributors:
        "data" in item.result
          ? item.result.data && "contributors" in item.result.data
            ? item.result.data.contributors
            : []
          : [],
    })),
  )
    .orderBy((item) => dayjs(item.updatedAt), "desc")
    .value()
  const lastVersion = lastVersions[0]
  const isFailed = lastVersion && lastVersion.status === "FAILED"
  const lastVersionCompleted = lastVersions.find(
    (item) => item.status === "COMPLETED",
  )
  const isRunning = lastVersion ? lastVersion.status === "PENDING" : false
  const tooltipLabel =
    category == BillyContributionAnalysisCategory.CONTRIBUTION_ANALYSIS
      ? isRunning
        ? "Analysis in progress"
        : lastVersion && !isFailed
          ? "Show Contributing Factors (Double click to trigger again)"
          : isFailed
            ? "Analysis failed: please contact support"
            : "Trigger Contributing Factors analysis"
      : isRunning
        ? "Analysis in progress"
        : lastVersion && !isFailed
          ? "Show History of Costs (Double click to trigger again)"
          : isFailed
            ? "Analysis failed: please contact support"
            : "Trigger History of Costs analysis"
  const colorIcon =
    lastVersion && !isFailed ? "success" : isFailed ? "error" : "textMuted"
  return (
    <>
      <Tooltip label={tooltipLabel}>
        <Indicator
          inline
          processing={isRunning}
          size={10}
          disabled={!isRunning}
          color={colorIcon}
        >
          <ActionIcon
            onClick={
              lastVersionCompleted
                ? () => {
                    setMarkdownContent(
                      convertSummaryContributingFactorsPartial(
                        lastVersionCompleted.summary || "",
                        lastVersionCompleted.contributors,
                      ),
                    )
                    if (lastVersionCompleted.summaryTitle) {
                      setSummaryValueFromBilly(
                        lastVersionCompleted.summaryTitle,
                      )
                    }
                  }
                : triggerAnalysis
            }
            onDoubleClick={triggerAnalysis}
            c={colorIcon}
            data-intercom-target="costory-tour-digest-contribution-wand"
          >
            {Icon}
          </ActionIcon>
        </Indicator>
      </Tooltip>
    </>
  )
}
const convertSummaryContributingFactors = (
  summary: string,
  contributors: BillyResponses.Contributors,
  digestPosition: Position,
  digestPeriod: {
    startDate: Dayjs
    endDate: Dayjs
  },
) => {
  const contributorToFilters = new Map<number, Filters>()
  for (const contributor of contributors) {
    contributorToFilters.set(
      contributor.hashes,
      getFiltersFromPosition({
        position: contributor.clean_contributors.concat(digestPosition),
        groupBy: "",
        period: {
          from: digestPeriod.startDate.format(DEFAULT_DATE_FORMAT),
          to: digestPeriod.endDate.format(DEFAULT_DATE_FORMAT),
        },
      }),
    )
  }
  const footnotes = []
  for (const contributor of contributors) {
    const filters = contributorToFilters.get(contributor.hashes)
    if (!filters) {
      continue
    }
    const hashText = `hash:<${contributor.hashes}>`
    const url = `${window.location.origin}/explorer?${getQueryFormatted(filters)}`
    if (summary.includes(hashText)) {
      footnotes.push(`[${contributor.hashes}]: ${url} 'Open'`)
    }
    summary = summary.replace(hashText, `([${contributor.hashes}])`)
  }
  return summary.concat("\n\n", footnotes.join("\n"))
}
